import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, type ElementRef } from '@angular/core';
import { type FormGroup } from '@angular/forms';

import { from, pipe, throwError, type Observable, type UnaryFunction } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { ScrollService } from '@fcom/common/services';
import { fileToBase64 } from '@fcom/common/utils/form-utils';
import { ConfigService, SentryLogger } from '@fcom/core/services';
import { retryWithBackoff } from '@fcom/rx';
import { type Attachment } from '@fcom/ui-components/components/interfaces';
import { LanguageService } from '@fcom/ui-translate';

import {
  CancellationCoverClaimFor,
  CertificatesForInsuranceCompanyField,
  ClaimMissingPartnerPointsFormField,
  type CancellationCoverClaimFormData,
  type CancellationCoverClaimResponseData,
  type CancellationCoverClaimSubmissionData,
  type CertificatesForInsuranceCompanyFormData,
  type CertificatesForInsuranceCompanyPayload,
  type CertificatesForInsuranceCompanyResponseData,
  type ClaimMissingPartnerPointsFormData,
  type ClaimMissingPartnerPointsResponseData,
  type RefundItem,
  type RefundRequestResponseData,
  type RequestRefundBankingData,
  type RequestRefundSubmissionData,
} from '../interfaces';
import { RefundPaymentMethods, RefundPaymentTypes, type RefundFormData } from '../interfaces/refund-form.interface';

type HttpPostOptions = Parameters<HttpClient['post']>[2];

@Injectable()
export class SubmitService {
  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private languageService: LanguageService,
    private scrollService: ScrollService
  ) {}
  public requestRefund(formValues: RefundFormData): Observable<RefundRequestResponseData> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerRefunds;
    const httpOptions: HttpPostOptions = {
      headers: new HttpHeaders({ 'x-api-key': keyId }),
    };
    const submitQuery = this.mapRefundRequestSubmissionData(formValues);
    return this.http.post<RefundRequestResponseData>(`${apiUrl}/refunds`, submitQuery, httpOptions).pipe(
      catchError((err: unknown) => {
        if (err instanceof Error) {
          this.sentryLogger.warn(err.message, { error: err });
          return throwError(() => err);
        }
      })
    );
  }

  public certificatesForInsuranceCompany(
    formData: CertificatesForInsuranceCompanyFormData
  ): Observable<CertificatesForInsuranceCompanyResponseData> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerCare;

    const body: CertificatesForInsuranceCompanyPayload = {
      bookingReference: formData[CertificatesForInsuranceCompanyField.BOOKING_REFERENCE],
      confirmSelfCancelledBooking: formData[CertificatesForInsuranceCompanyField.CONFIRM_SELF_CANCELLED_BOOKING],
      email: formData[CertificatesForInsuranceCompanyField.EMAIL],
      firstName: formData[CertificatesForInsuranceCompanyField.FIRST_NAME],
      flights: formData[CertificatesForInsuranceCompanyField.FLIGHTS],
      lastName: formData[CertificatesForInsuranceCompanyField.LAST_NAME],
      locale: this.languageService.langKeyValue,
      phone: formData[CertificatesForInsuranceCompanyField.PHONE],
      reasonForCertificate: formData[CertificatesForInsuranceCompanyField.REASON_FOR_CERTIFICATE],
      ticketNumber: formData[CertificatesForInsuranceCompanyField.TICKET_NUMBER],
      worldTracerNumber: formData[CertificatesForInsuranceCompanyField.WORLD_TRACER_NUMBER],
    };

    const httpOptions: HttpPostOptions = {
      headers: new HttpHeaders({ 'x-api-key': keyId }),
    };

    return this.http
      .post<CertificatesForInsuranceCompanyResponseData>(
        `${apiUrl}/feedback/certificates-for-insurance-company`,
        body,
        httpOptions
      )
      .pipe(
        catchError((error: unknown) => {
          this.sentryLogger.warn('Error submitting CancellationVerification request', { error });
          return throwError(() => error);
        })
      );
  }

  public sendCancellationCoverClaimRequest(
    formValues: CancellationCoverClaimFormData,
    file: File
  ): Observable<CancellationCoverClaimResponseData> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerCare;
    const httpOptions: HttpPostOptions = {
      headers: new HttpHeaders({
        'x-api-key': keyId,
      }),
    };
    return fileToBase64(file).pipe(
      switchMap((base64File: string): Observable<CancellationCoverClaimResponseData> => {
        const body: CancellationCoverClaimSubmissionData = this.mapCancellationCoverClaimSubmissionData(
          formValues,
          base64File,
          file.name
        );
        return this.http
          .post<CancellationCoverClaimResponseData>(`${apiUrl}/feedback/cancellation-cover-claim`, body, httpOptions)
          .pipe(
            catchError((error): Observable<never> => {
              this.sentryLogger.warn(error?.message, { error });
              return throwError(() => error);
            })
          );
      })
    );
  }

  public claimMissingPartnerPoints(
    formValues: ClaimMissingPartnerPointsFormData
  ): Observable<ClaimMissingPartnerPointsResponseData> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.portalServices;

    const fields: ClaimMissingPartnerPointsFormField[] = [
      ClaimMissingPartnerPointsFormField.MEMBER_NAME,
      ClaimMissingPartnerPointsFormField.MEMBER_NUMBER,
      ClaimMissingPartnerPointsFormField.EMAIL,
      ClaimMissingPartnerPointsFormField.ATTACHMENT,
      ClaimMissingPartnerPointsFormField.ADDITIONAL_INFORMATION,
    ];

    const formData: FormData = new FormData();
    formData.append('__addressKey', 'emailServlet.plusPlusshop');
    formData.append('__messageSubject', 'Claim missing Finnair Plus partner points');
    formData.append('__confirmationTo', formValues.claimMissingPartnerPoints[ClaimMissingPartnerPointsFormField.EMAIL]);
    formData.append('__locale', this.languageService.langKeyValue);

    for (const field of fields) {
      formData.append(
        field,
        field === ClaimMissingPartnerPointsFormField.ATTACHMENT &&
          'string' !== typeof formValues.claimMissingPartnerPoints[field]
          ? Array.isArray(formValues.claimMissingPartnerPoints[field])
            ? formValues.claimMissingPartnerPoints[field]?.[0]?.file
            : (formValues.claimMissingPartnerPoints[field] as Attachment)?.file
          : (formValues.claimMissingPartnerPoints[field] as string)
      );
    }

    const httpOptions: HttpPostOptions = {
      headers: new HttpHeaders({
        keyId,
      }),
    };

    return this.http.post<ClaimMissingPartnerPointsResponseData>(`${apiUrl}/mailer`, formData, httpOptions).pipe(
      catchError((error: unknown): Observable<never> => {
        this.sentryLogger.warn('Error submitting ClaimMissingPartnerPoints request', { error });
        return throwError((): unknown => error);
      })
    );
  }

  /*
   * This is a ridiculous hack but that's how the API works...
   * API starts processing after the first request and returns 201 until the processing is done
   * Processing can take up to 5 minutes so that's why we have maximum of 150 attemps
   * We throw an error and retry until we have received some content.
   * Also we don't want to retry 150 times if server returns an error
   */
  public retryWhenOKResponseWithEmptyBody<A extends { feedbackCase: unknown }>(): UnaryFunction<
    Observable<A>,
    Observable<A>
  > {
    const MAX_ATTEMPTS = 150;
    const BACKOFF_MS = 2000;
    return pipe(
      mergeMap<A, Observable<A>>((response) => {
        if (response?.feedbackCase) {
          return from([response]);
        }
        return throwError(() => new Error('Empty body'));
      }),
      retryWithBackoff<A>(MAX_ATTEMPTS, BACKOFF_MS)
    );
  }

  public buildHeaders(params: object): HttpHeaders {
    return new HttpHeaders(
      Object.assign(
        {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
        params
      )
    );
  }

  private mapCancellationCoverClaimSubmissionData(
    formValues: CancellationCoverClaimFormData,
    fileContent: string,
    fileName: string
  ): CancellationCoverClaimSubmissionData {
    return {
      bookingReference: formValues.bookingReference,
      ticketNumber: formValues.ticketNumber,
      departureCity: formValues.departureCity && formValues.departureCity.title,
      destinationCity: formValues.destinationCity && formValues.destinationCity.title,
      departureDate: formValues.departureDate ? formValues.departureDate.toDottedString() : undefined,
      returnDate: formValues.returnDate ? formValues.returnDate.toDottedString() : undefined,
      firstName: formValues.firstName,
      familyName: formValues.familyName,
      email: formValues.email,
      country: formValues.country,
      phone: formValues.phoneNumber,
      refundFor:
        formValues.refundFor === 'returnFlight'
          ? CancellationCoverClaimFor.RETURN_FLIGHTS
          : CancellationCoverClaimFor.DEPARTURE_AND_RETURN_FLIGHTS,
      attachment: fileContent,
      attachmentConsent: formValues.attachmentConsent ? 'accepted' : 'not accepted',
      attachmentName: fileName,
      iban: formValues.iban,
      bic: formValues.bic,
      accountHolder: formValues.accountHolder,
      bankName: formValues.bankName,
      companions: formValues.companions?.map((companion) => ({
        firstName: companion.firstName,
        familyName: companion.familyName,
      })),
      locale: this.languageService.langKeyValue,
    };
  }

  private mapRefundRequestSubmissionData(formValues: RefundFormData): RequestRefundSubmissionData {
    enum RefundType {
      flight = 'Flight Ticket',
      extras = 'Travel Extra',
      flightAndExtras = 'Flight Ticket and Travel Extra',
      travelRetail = 'Travel Retail',
    }

    enum RefundReason {
      flightCancelledByFinnair = 'Involuntary',
      flightCancelledWithin24Hours = '24h Rule',
      extraNotDelivered = 'Ancillary Invol',
      changedFlightOrSeat = 'Upgrade',
      ticketAllowsCancellation = 'Voluntary',
      bestBeforeExpire = 'Expired Date',
      faultyProduct = 'Faulty',
      wrongProduct = 'Wrong Product',
      // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
      cancelledMyFlight = 'Voluntary',
      // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
      orderNotDelivered = 'Involuntary',
    }

    enum RefundItemType {
      baggage = 'Baggage',
      internet = 'Internet',
      lounge = 'Lounge',
      comfortItems = 'Comfort items',
      meal = 'Meal',
      seat = 'Seat',
      seatAndMeal = 'Seat and meal',
    }

    const mainPax = formValues.passengers[0];
    let output: RequestRefundSubmissionData = {
      refundType: RefundType[formValues.refundType],
      refundReason: RefundReason[formValues.refundReason],
      firstName: mainPax.firstname,
      lastName: mainPax.lastname,
      phone: formValues.phoneNumber,
      email: formValues.email,
      finnairPlusNumber: mainPax.finnairPlusNumber,
      flightNumber: formValues.flightNumber,
      bookingReference: formValues.reservationNumber,
      refundItems: [],
      useOnlyVoucher: formValues.paymentMethod === RefundPaymentMethods.GIFT_VOUCHER,
    };

    if (formValues.flightDate) {
      output.departureDate = formValues.flightDate.toString();
    }

    if (formValues.bookingDate) {
      output.bookingDate = formValues.bookingDate.toString();
    }

    if (formValues.cancellationDate) {
      output.cancellationDate = formValues.cancellationDate.toString();
    }

    if (formValues.additionalInformation) {
      output.additionalInformation = formValues.additionalInformation;
    }

    formValues.passengers.forEach(function (passenger) {
      const baseData: RefundItem = { firstName: passenger.firstname, lastName: passenger.lastname };
      passenger.finnairPlusNumber && (baseData.finnairPlusNumber = passenger.finnairPlusNumber);

      // add preorderBagNumber to RefundItem (if available)
      if (passenger.preorderBagNumber) {
        baseData.preorderBagNumber = passenger.preorderBagNumber;
        output.refundItems.push(baseData);
      }

      // create refund item for flight ticket (if available)
      if (passenger.ticketNumber) {
        const flightData = { ticketNumber: passenger.ticketNumber };
        output.refundItems.push({ ...baseData, ...flightData });
      }

      // create refund item for each travel extra (if available)
      if (passenger.travelExtras) {
        passenger.travelExtras.forEach(function (travelExtra) {
          const emdData = {
            emdNumber: travelExtra.emdNumber,
            refundItemType: RefundItemType[travelExtra.refundItemType],
          };
          output.refundItems.push({ ...baseData, ...emdData });
        });
      }
    });

    if (formValues.paymentType === RefundPaymentTypes.ONLINE_BANK) {
      output = { ...output, ...this.mapOnlinePaymentData(formValues) };
    }

    if (!output.flightNumber) {
      delete output.flightNumber;
    }

    return output;
  }

  private mapOnlinePaymentData(formValues: RefundFormData): RequestRefundBankingData {
    const bankingData: RequestRefundBankingData = {};

    formValues.iban && (bankingData.iban = formValues.iban);
    formValues.accountNumber && (bankingData.iban = formValues.accountNumber);
    formValues.bic && (bankingData.bic = formValues.bic);
    formValues.accountHolder && (bankingData.bankAccountHolder = formValues.accountHolder);
    formValues.accountHolderAddress && (bankingData.bankAccountHolderAddress = formValues.accountHolderAddress);
    formValues.bankName && (bankingData.bankName = formValues.bankName);
    formValues.bankAddress && (bankingData.bankAddress = formValues.bankAddress);
    formValues.correspondentBankData && (bankingData.correspondentBankData = formValues.correspondentBankData);
    formValues.bik && (bankingData.bik = formValues.bik);
    formValues.inn && (bankingData.inn = formValues.inn);
    formValues.patronymicName && (bankingData.patronymicName = formValues.patronymicName);
    formValues.kpp && (bankingData.kpp = formValues.kpp);
    formValues.ncc && (bankingData.ncc = formValues.ncc);
    formValues.ifsc && (bankingData.ifsc = formValues.ifsc);

    return bankingData;
  }

  public scrollToInvalidField = (form: FormGroup | FormGroup[], elementRef: ElementRef): void => {
    if (Array.isArray(form)) {
      form.forEach((f: FormGroup): void => {
        f.markAllAsTouched();
        f.updateValueAndValidity();
      });
    } else {
      form.markAllAsTouched();
      form.updateValueAndValidity();
    }
    setTimeout((): void => {
      this.scrollService.scrollToFirstInvalidInput(elementRef, 40);
    });
  };
}
